/**
 * Listing photos — small surface: read URLs from row/json, map storage paths to public URLs, optional signed fallback.
 */

import { supabase } from './supabase';

/** First non-empty gallery field on a listing-shaped row. */
export function coalesceListingMediaRaw(row: unknown): unknown {
  if (row == null || typeof row !== 'object') return null;
  const r = row as Record<string, unknown>;
  return (
    r.images ??
    r.image_urls ??
    r.photos ??
    r.media ??
    r.image ??
    r.image_url ??
    r.thumbnail_url ??
    r.primary_image ??
    r.cover_image ??
    null
  );
}

function collectUrlStrings(x: unknown, acc: string[]): void {
  if (x == null) return;
  if (typeof x === 'string') {
    const s = x.trim();
    if (s) acc.push(s);
    return;
  }
  if (Array.isArray(x)) {
    for (const el of x) collectUrlStrings(el, acc);
    return;
  }
  if (typeof x === 'object') {
    const o = x as Record<string, unknown>;
    const direct = [o.url, o.src, o.href, o.publicUrl, o.path, o.image, o.thumbnail, o.file].find(
      (v) => typeof v === 'string' && String(v).trim()
    );
    if (direct) {
      acc.push(String(direct).trim());
      return;
    }
    for (const v of Object.values(o)) collectUrlStrings(v, acc);
  }
}

function parseStorageRef(url: string): { bucket: string; path: string } | null {
  const u = url.trim();
  const patterns = [
    /\/storage\/v1\/object\/(?:public|sign)\/([^/]+)\/([^?#]+)/i,
    /\/storage\/v1\/render\/image\/(?:public|sign)\/([^/]+)\/([^?#]+)/i,
  ];
  for (const re of patterns) {
    const m = u.match(re);
    if (!m) continue;
    try {
      return { bucket: m[1], path: decodeURIComponent(m[2]) };
    } catch {
      return { bucket: m[1], path: m[2] };
    }
  }
  return null;
}

function listingsObjectKey(key: string): string {
  const k = key.replace(/^\/+/, '');
  if (/^(listings|staff-import|receipts)\//i.test(k)) return k;
  return `listings/${k}`;
}

function postsObjectKey(key: string): string {
  const k = key.replace(/^\/+/, '');
  if (/^posts\//i.test(k)) return k;
  return `posts/${k}`;
}

function toPublicUrl(s: string): string {
  const t = s.trim();
  if (!t) return '';
  if (/^https?:\/\//i.test(t)) return t;
  if (/^\/\//.test(t)) return `https:${t}`;
  if (/^(data:|blob:)/i.test(t)) return t;
  const key = t.replace(/^\/+/, '');
  const usePosts = /^posts\//i.test(key);
  const bucket = usePosts ? 'posts' : 'listings';
  const objectKey = usePosts ? postsObjectKey(key) : listingsObjectKey(key);
  return supabase.storage.from(bucket).getPublicUrl(objectKey).data.publicUrl;
}

/** Flatten jsonb / arrays / nested objects into unique browser-ready URLs. */
export function normalizeListingImageUrls(raw: unknown): string[] {
  if (raw == null) return [];
  const acc: string[] = [];
  if (typeof raw === 'string') {
    const s = raw.trim();
    if (!s) return [];
    try {
      collectUrlStrings(JSON.parse(s) as unknown, acc);
    } catch {
      acc.push(s);
    }
  } else {
    collectUrlStrings(raw, acc);
  }
  const seen = new Set<string>();
  const out: string[] = [];
  for (const u of acc) {
    const x = toPublicUrl(u);
    if (x && !seen.has(x)) {
      seen.add(x);
      out.push(x);
    }
  }
  return out;
}

export function normalizeListingImagesFromRow(row: unknown): string[] {
  return normalizeListingImageUrls(coalesceListingMediaRaw(row));
}

/** Signed GET when public URL fails (private bucket / RLS). */
export async function trySignedListingImageUrl(displayUrl: string): Promise<string | null> {
  const u = displayUrl.trim();
  if (!u) return null;
  const parsed = parseStorageRef(u);
  if (parsed) {
    let { bucket, path } = parsed;
    if (bucket === 'listings') path = listingsObjectKey(path);
    else if (bucket === 'posts') path = postsObjectKey(path);
    const { data, error } = await supabase.storage.from(bucket).createSignedUrl(path, 3600);
    if (!error && data?.signedUrl) return data.signedUrl;
    return null;
  }
  if (!/^https?:\/\//i.test(u)) {
    const usePosts = /^posts\//i.test(u);
    const bucket = usePosts ? 'posts' : 'listings';
    const path = usePosts ? postsObjectKey(u) : listingsObjectKey(u);
    const { data, error } = await supabase.storage.from(bucket).createSignedUrl(path, 3600);
    if (!error && data?.signedUrl) return data.signedUrl;
  }
  return null;
}
